import React, { useState, useEffect } from "react";
import { closeAddBankModal } from "../../features/slices/ProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import { addBankDetails, reset } from "../../features/slices/PaymentSlice";
import { useMediaQuery } from "react-responsive";

const ALPHA_NAME_REGEX = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
const NUMERIC_REGEX = /^\d+$/;
// Same limits as POST /player/bank-accounts.
const ACCOUNT_NUMBER_REGEX = /^\d{9,18}$/;
const MIN_ACCOUNT_NUMBER_LENGTH = 9;
const MAX_ACCOUNT_NUMBER_LENGTH = 18;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// The first 4 letters of an IFSC identify the bank.
const BANK_NAMES_BY_IFSC_PREFIX = {
  SBIN: "State Bank of India",
  HDFC: "HDFC Bank",
  ICIC: "ICICI Bank",
  UTIB: "Axis Bank",
  KKBK: "Kotak Mahindra Bank",
  PUNB: "Punjab National Bank",
  BARB: "Bank of Baroda",
  CNRB: "Canara Bank",
  UBIN: "Union Bank of India",
  BKID: "Bank of India",
  IDIB: "Indian Bank",
  IOBA: "Indian Overseas Bank",
  YESB: "Yes Bank",
  INDB: "IndusInd Bank",
  IDFB: "IDFC First Bank",
  FDRL: "Federal Bank",
};

// POST /player/bank-accounts requires bank_name, but the form does not ask for it.
const getBankName = (ifsc) => {
  const prefix = ifsc.slice(0, 4);
  return BANK_NAMES_BY_IFSC_PREFIX[prefix] || prefix;
};

/** Limits are POST /player/bank-accounts'; the 2 minimum is ours. */
const getFieldErrors = ({ name, account, ifsc }) => {
  const errors = {};
  const holderName = name.trim();
  const accountNumber = account.trim();

  if (!holderName) {
    errors.name = "Please enter account holder name";
  } else if (!ALPHA_NAME_REGEX.test(holderName)) {
    errors.name = "Account holder name must contain only letters";
  } else if (holderName.length < 2 || holderName.length > 255) {
    errors.name = "Account holder name must be 2 to 255 characters";
  }

  if (!accountNumber) {
    errors.account = "Please enter account number";
  } else if (!NUMERIC_REGEX.test(accountNumber)) {
    errors.account = "Account number must contain only digits";
  } else if (!ACCOUNT_NUMBER_REGEX.test(accountNumber)) {
    errors.account = `Account number must be between ${MIN_ACCOUNT_NUMBER_LENGTH} and ${MAX_ACCOUNT_NUMBER_LENGTH} digits long`;
  }

  if (!ifsc.trim()) {
    errors.ifsc = "Please enter IFSC code";
  } else if (!IFSC_REGEX.test(ifsc.trim())) {
    errors.ifsc = "IFSC code is not valid, for example SBIN0012451";
  }

  return errors;
};

export default function NewBankAccount() {
  const dispatch = useDispatch();
  const payment = useSelector((state) => state.payment);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [ifsc, setIfsc] = useState("");
  const [account, setAccount] = useState("");
  const [name, setPersonName] = useState("");
  // Errors stay hidden until a field is left or the form is submitted, so
  // nobody is told off while still typing.
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const fieldErrors = getFieldErrors({ name, account, ifsc });

  const visibleError = (field) =>
    submitted || touched[field] ? fieldErrors[field] : "";

  const touchField = (field) => () =>
    setTouched((fields) => ({ ...fields, [field]: true }));

  const inputClass = (field) =>
    `form-control ${visibleError(field) ? "field-invalid" : ""}`;

  const fieldError = (field) =>
    visibleError(field) ? (
      <p className="field-error" role="alert">
        {visibleError(field)}
      </p>
    ) : null;

  const closeModal = () => {
    dispatch(closeAddBankModal());
  };

  const addNewBank = (e) => {
    e.preventDefault();
    dispatch(reset());

    setSubmitted(true);
    if (Object.keys(fieldErrors).length > 0) return;

    dispatch(
      addBankDetails({
        account_holder_name: name.trim(),
        account_number: account.trim(),
        ifsc_code: ifsc.trim().toUpperCase(),
        bank_name: getBankName(ifsc.trim().toUpperCase()),
      })
    );
  };

  const setName = (e) => {
    setPersonName(e.target.value.replace(/[^A-Za-z\s]/g, ""));
  };

  const setAccountNo = (e) => {
    setAccount(
      e.target.value.replace(/\D/g, "").slice(0, MAX_ACCOUNT_NUMBER_LENGTH)
    );
  };

  const handleIfscChange = (e) => {
    setIfsc(
      e.target.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 11)
    );
  };

  const isIfscValid = IFSC_REGEX.test(ifsc);

  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  return (
    <div
      className="modal fade show new-account"
      style={{
        display: "block",
        backdropFilter: "blur(15px)",
        background: "rgb(70, 70, 70, 0.6)",
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title fs-5">Add bank details</h2>
            <i onClick={closeModal} className="bi bi-x-lg"></i>
          </div>
          <div className="modal-body p-4">
            <form className="needs-validation" onSubmit={addNewBank} noValidate>
              {payment.error ? (
                <div
                  className="mt-4 p-3 border border-light border-opacity-25 rounded mb-3"
                  style={{
                    color: "rgb(255, 120, 120)",
                    background: "rgb(20,20,20)",
                  }}
                >
                  <p className="text--medium mb-0">
                    <i className="bi bi-exclamation-circle"></i> Error:{" "}
                    {payment.error}
                  </p>
                </div>
              ) : null}
              <h6 className="mb-4">Add Bank Account</h6>
              <div className="form">
                <div className="row">
                  <div className="col-md-12 col-lg-12 col-sm-12 col-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      Account Holder Name
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Account Holder Name"
                      value={name}
                      className={inputClass("name")}
                      onChange={setName}
                      onBlur={touchField("name")}
                      required
                    />
                    {fieldError("name")}
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-md-6 col-sm-12 col-lg-6">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      Account Number
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter Account Number"
                      value={account}
                      onChange={setAccountNo}
                      onBlur={touchField("account")}
                      minLength={MIN_ACCOUNT_NUMBER_LENGTH}
                      maxLength={MAX_ACCOUNT_NUMBER_LENGTH}
                      required
                      className={inputClass("account")}
                    />
                    {fieldError("account")}
                  </div>
                  <div className="col-md-6 col-sm-12 col-lg-6">
                    <label
                      className="text--medium mb-3 if--sc--label"
                      style={{ fontWeight: "200" }}
                    >
                      IFSC Code
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <div className="flex if--info">
                      <input
                        type="text"
                        placeholder="Enter IFSC"
                        value={ifsc}
                        required
                        className={inputClass("ifsc")}
                        onChange={handleIfscChange}
                        onBlur={touchField("ifsc")}
                      />
                      {isIfscValid ? (
                        <i className="bi bi-check2 mx-2"></i>
                      ) : null}
                    </div>
                    {fieldError("ifsc")}
                  </div>
                </div>
              </div>
              <div className="text-end">
                <button
                  className={`standard-btn add-new-bank mt-4 mb-2 ${
                    isMobile ? "w-100" : "w-50"
                  }`}
                  type="submit"
                >
                  Add Bank Account
                </button>
                <p
                  className="text--medium"
                  style={{ fontWeight: "300", color: "#E42D2D" }}
                >
                  Note: Bank account will be available after verification
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
